/* Akor Kulağı (şarkıyı akorla çal) — ders içerikleri. Blok türleri ve satır içi işaretler: ./blocks.ts */
import { cell, compare, go, h, type Lesson, list, p, play, steps, table, tip } from './blocks';

export const akor: Lesson[] = [
  {
    id: 'a1',
    title: 'Solo kulağından akor kulağına',
    minutes: 5,
    summary: `Melodiyi kulaktan çalabiliyorsan, akorları çıkarmak için gereken becerinin önemli bir kısmına zaten sahipsin.`,
    blocks: [
      p(
        `Melodi tek tek çalınan notalardan oluşur. **Akor** ise aynı anda çalınan üç ya da daha fazla notadır. Akorları çıkarmak ilk başta zor görünür, çünkü kulak aynı anda birden çok sesi ayırmaya alışık değildir.`,
      ),
      compare([
        ['Melodi (notalar tek tek)', { seq: 'C4 E4 G4:2' }],
        ['Akor (aynı anda)', { chord: 'C' }],
      ]),
      h(`İyi haber: bas notası`),
      p(
        `Çoğu şarkıda akorun **kök notası** en kalın seste, yani basta çalınır. Solo çıkarabiliyorsan bas hattını da çıkarabilirsin: o da bir melodidir, yalnızca daha kalındır.`,
      ),
      steps([
        [
          'Bası dinle',
          `Şarkıdaki en kalın sese odaklan. Sesi kısıp dinlemek de işe yarar; bas, kısık seste bile duyulur.`,
        ],
        ['Mırıldan', `Bas notasını kendi rahat aralığında mırıldan. Hangi oktavda söylediğin önemli değil.`],
        [
          '6. ve 5. telde bul',
          `Mırıldandığın notayı 6. ya da 5. telde ara. Bulduğun nota büyük ihtimalle akorun köküdür.`,
        ],
        [
          'Majör mü minör mü?',
          `Kökü bulduktan sonra o notadan majör ve minör akoru ayrı ayrı çal; duyduğuna hangisi uyuyorsa akor odur.`,
        ],
      ]),
      play('Bas notalarını takip et', { prog: 'G Em C D', bpm: 90 }),
      tip(
        'info',
        'Bu dizide',
        `Bas notaları {{pc:G}} – {{pc:E}} – {{pc:C}} – {{pc:D}} şeklinde ilerliyor. Akorlar da {{c:G}} – {{c:Em}} – {{c:C}} – {{c:D}}.`,
      ),
      {
        t: 'quiz',
        q: `Bu akorun kökü hangisi olabilir? En kalın sese odaklan ve notalarla karşılaştır.`,
        items: [
          { label: 'Akor', chord: 'A' },
          { label: '{{pc:A}}', seq: 'A2:2' },
          { label: '{{pc:E}}', seq: 'E2:2' },
          { label: '{{pc:D}}', seq: 'D3:2' },
        ],
        options: ['{{pc:A}}', '{{pc:E}}', '{{pc:D}}'],
        answer: 0,
        explain: `Bu bir {{c:A}} akoru. En kalın sesi 5. telin boş sesi, yani {{pc:A}}.`,
      },
      go('Kök Notayı Bul', 'akor.egz.kok.1'),
    ],
  },
  {
    id: 'a2',
    title: 'Majör ve minör',
    minutes: 6,
    summary: `Majör ve minör akorlar arasındaki tek nota farkını hem teoride hem kulakta tanı.`,
    blocks: [
      p(
        `En temel akorlar üç notadan oluşur ve **üçlü akor** diye adlandırılır. Majör ve minör akor arasındaki fark tek bir notadadır, ama duyguları çok farklıdır: majör daha aydınlık ve açık, minör daha hüzünlü ve içe dönük duyulur.`,
      ),
      table(
        ['Akor', 'Formül', 'Örnek', 'Dinle'],
        [
          ['Majör', "Kök + büyük 3'lü + tam 5'li", '{{c:C}} = {{pc:C}} {{pc:E}} {{pc:G}}', cell({ chord: 'C' })],
          ['Minör', "Kök + küçük 3'lü + tam 5'li", '{{c:Cm}} = {{pc:C}} {{pc:Eb}} {{pc:G}}', cell({ chord: 'Cm' })],
        ],
      ),
      p(`Majörde kökten 3'lüye 4 yarım ses, minörde 3 yarım ses vardır. 5'li ikisinde de aynıdır.`),
      compare([
        ['C · majör', { chord: 'C' }],
        ['Cm · minör', { chord: 'Cm' }],
      ]),
      compare([
        ['A · majör', { chord: 'A' }],
        ['Am · minör', { chord: 'Am' }],
      ]),
      compare([
        ['E · majör', { chord: 'E' }],
        ['Em · minör', { chord: 'Em' }],
      ]),
      {
        t: 'chords',
        names: 'A Am E Em',
        caption: `Gitarda fark tek parmaktadır: {{c:A}} ile {{c:Am}} ve {{c:E}} ile {{c:Em}} arasında yalnızca 3'lü notası değişir.`,
      },
      tip(
        'info',
        'Duyguya güvenme, karşılaştır',
        `"Mutlu–hüzünlü" ayrımı başlangıç için iyi bir rehberdir, ama hızlı ve enerjik minör şarkılar da vardır. Emin olamadığında akorun hem majör hem minör halini çal ve duyduğunla karşılaştır.`,
      ),
      {
        t: 'quiz',
        q: `Bu akor majör mü, minör mü?`,
        play: { chord: 'Dm' },
        options: ['Majör', 'Minör'],
        answer: 1,
        explain: `Bu {{c:Dm}}: {{pc:D}} {{pc:F}} {{pc:A}}. Kökle 3'lü arasında 3 yarım ses var, yani minör.`,
      },
      {
        t: 'quiz',
        q: `Peki bu?`,
        play: { chord: 'G' },
        options: ['Majör', 'Minör'],
        answer: 0,
        explain: `Bu {{c:G}}: {{pc:G}} {{pc:B}} {{pc:D}}. Kökle 3'lü arasında 4 yarım ses var, yani majör.`,
      },
      go('Majör mü Minör mü? Seviye 1', 'akor.egz.kalite.1'),
      go('Akorlar neden biraz falso?', 'az-bilinenler.esit-tamperaman'),
    ],
  },
  {
    id: 'a3',
    title: 'Tonalitenin akor ailesi',
    minutes: 7,
    summary: `Bir tonalitede hangi akorların "aileden" olduğunu bilmek, akor çıkarırken tahmin listesini yediye indirir.`,
    blocks: [
      p(
        `Bir şarkı belli bir tonalitedeyse, akorlarının çoğu o tonalitenin gamındaki notalardan kurulur. Majör gamın her notası üzerine bir üçlü akor kurduğunda yedi akorluk bir aile elde edersin. Bunlara **diyatonik akorlar** denir.`,
      ),
      table(
        ['Derece', 'I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'],
        [
          ['Tür', 'Majör', 'Minör', 'Minör', 'Majör', 'Majör', 'Minör', 'Eksik'],
          ['{{pc:C}} majör', '{{c:C}}', '{{c:Dm}}', '{{c:Em}}', '{{c:F}}', '{{c:G}}', '{{c:Am}}', '{{c:Bdim}}'],
          ['{{pc:G}} majör', '{{c:G}}', '{{c:Am}}', '{{c:Bm}}', '{{c:C}}', '{{c:D}}', '{{c:Em}}', '{{c:F#dim}}'],
          ['{{pc:D}} majör', '{{c:D}}', '{{c:Em}}', '{{c:F#m}}', '{{c:G}}', '{{c:A}}', '{{c:Bm}}', '{{c:C#dim}}'],
          ['{{pc:A}} majör', '{{c:A}}', '{{c:Bm}}', '{{c:C#m}}', '{{c:D}}', '{{c:E}}', '{{c:F#m}}', '{{c:G#dim}}'],
          ['{{pc:E}} majör', '{{c:E}}', '{{c:F#m}}', '{{c:G#m}}', '{{c:A}}', '{{c:B}}', '{{c:C#m}}', '{{c:D#dim}}'],
        ],
      ),
      p(
        `Büyük Romen rakamları majör, küçükler minör akorları gösterir. ° işareti **eksik** (diminished) akoru gösterir; pop ve rock şarkılarında nadiren kullanılır.`,
      ),
      tip(
        'success',
        'Kalıp hiç değişmez',
        `Her majör tonalitede **I, IV ve V** majördür; **ii, iii ve vi** minördür. Çok sayıda şarkı yalnızca I, IV, V ve vi akorlarıyla yazılmıştır.`,
      ),
      play('{{pc:G}} majör ailesi: I – ii – iii – IV – V – vi', { prog: 'G Am Bm C D Em', bpm: 110 }),
      h(`Minör tonalite`),
      p(
        `Minör tonalitede aile şöyledir: **i – ii° – III – iv – v – VI – VII**. {{pc:A}} minörde: {{c:Am}} {{c:Bdim}} {{c:C}} {{c:Dm}} {{c:Em}} {{c:F}} {{c:G}}. Dikkat edersen bunlar {{pc:C}} majörün akorlarıyla aynıdır; değişen, hangi akorun "ev" olduğudur.`,
      ),
      tip(
        'info',
        'Minörde V çoğu zaman majördür',
        `Minör şarkılarda v akoru çoğu zaman majör çalınır, çünkü majör V toniğe çok daha güçlü bir çekim yaratır. {{pc:A}} minörde {{c:Em}} yerine {{c:E}} ya da {{c:E7}} duyarsın. Türk pop ve rock müziğinde bu çok yaygındır.`,
      ),
      compare([
        ['Am – Em – Am', { prog: 'Am Em Am' }],
        ['Am – E – Am', { prog: 'Am E Am' }],
      ]),
      {
        t: 'quiz',
        q: `{{pc:D}} majör tonalitesinde IV akoru hangisi?`,
        options: ['{{c:G}}', '{{c:A}}', '{{c:Em}}'],
        answer: 0,
        explain: `{{pc:D}} majör gamı: {{pc:D}} {{pc:E}} {{pc:F#}} {{pc:G}} {{pc:A}} {{pc:B}} {{pc:C#}}. Dördüncü nota {{pc:G}}, IV akoru da {{c:G}} majör.`,
      },
      go('Derece Tanıma: I, IV, V', 'akor.egz.derece.1'),
      go('Minör tonaliteler', 'akor.egz.derece.4'),
    ],
  },
  {
    id: 'a4',
    title: 'Akor fonksiyonları',
    minutes: 5,
    summary: `Akorların bir tonalitede üstlendiği üç temel rolü, yani tonik, subdominant ve dominantı kulakla tanı.`,
    blocks: [
      p(
        `Diyatonik akorların her birinin bir görevi vardır. Onları üç gruba ayırmak, bir dizide neler olup bittiğini duymayı kolaylaştırır.`,
      ),
      table(
        ['Fonksiyon', 'Akorlar', 'Nasıl hissettirir?'],
        [
          ['**Tonik** (ev)', 'I, vi (iii)', 'Dinlenme, varış, huzur.'],
          ['**Subdominant** (yolculuk)', 'IV, ii', 'Evden uzaklaşma, açılma, hareket.'],
          ['**Dominant** (gerilim)', 'V, V7, vii°', 'Gerilim; toniğe dönmek ister.'],
        ],
      ),
      play('I – IV – V – I', { prog: 'C F G C', bpm: 90 }),
      p(
        `Bu dizide {{c:C}} ile başlarsın (ev), {{c:F}} ile yola çıkarsın, {{c:G}} ile gerilim yükselir ve son {{c:C}} ile eve dönersin. Batı müziğinin en temel hikayesi budur.`,
      ),
      h(`Dominant 7'li: en güçlü çekim`),
      p(
        `V akoruna 7'li eklediğinde ({{c:G7}}) gerilim daha da artar. {{c:G7}} içindeki {{pc:B}} notası {{pc:C}} notasına çıkmak, {{pc:F}} notası da {{pc:E}} notasına inmek ister. Bu iki hareket akoru neredeyse kaçınılmaz olarak {{c:C}} akoruna götürür.`,
      ),
      compare([
        ['G → C', { prog: 'G C' }],
        ['G7 → C', { prog: 'G7 C' }],
      ]),
      tip(
        'info',
        'Kulakla nasıl kullanılır?',
        `Bir şarkıda bir yere varmak isteyen, gergin bir akor duyduğunda büyük ihtimalle V akorudur. Hemen ardından gelen rahatlama da genelde I akorudur.`,
      ),
      {
        t: 'quiz',
        q: `Dizinin son akoru "ev" hissi veriyor mu?`,
        play: { prog: 'C F C G' },
        options: ['Evet, eve dönmüş', 'Hayır, gerilimde kalmış'],
        answer: 1,
        explain: `Dizi {{c:G}} ile, yani V akoruyla bitiyor. Gerilim çözülmeden kalıyor ve kulak bir {{c:C}} daha bekliyor.`,
      },
      go('Derece Tanıma: I, IV, V, vi', 'akor.egz.derece.2'),
    ],
  },
  {
    id: 'a5',
    title: 'Bas hattını takip et',
    minutes: 5,
    summary: `Akor çıkarmanın en güvenilir yolu bas hattını bulmaktır; çevrimleri ve bölü akorları da tanı.`,
    blocks: [
      p(
        `Bir şarkıdaki akorları çıkarırken işe her zaman bastan başla. Bas genellikle akorun kökünü çalar ve akor değişimlerini en net gösteren şey bas hareketidir.`,
      ),
      steps([
        [
          'Değişim anını bul',
          `Bas notası değiştiğinde büyük ihtimalle akor da değişmiştir. Önce yalnızca "ne zaman değişiyor?" sorusuna odaklan.`,
        ],
        [
          'Bas notalarını yaz',
          `Her değişimdeki bas notasını kalın tellerde bul ve sırayla not et. Örneğin: {{pc:A}} – {{pc:F}} – {{pc:C}} – {{pc:G}}.`,
        ],
        [
          'Akor türüne karar ver',
          `Her bas notası için tonalitenin akor ailesine bak. {{pc:C}} majör ailesinde {{pc:A}} üzerindeki akor {{c:Am}}, {{pc:F}} üzerindeki {{c:F}} olur.`,
        ],
        [
          'Çalarak doğrula',
          `Bulduğun akorları duyduğunla birlikte çal. Uymayan bir akor varsa önce türünü (majör/minör), sonra bası yeniden kontrol et.`,
        ],
      ]),
      play('Bası takip et', { prog: 'Am F C G', bpm: 90 }),
      h(`Bas her zaman kök değildir`),
      p(
        `Bazen akorun kendisi aynı kalır ama basta başka bir akor notası çalınır. Buna **çevrim** denir ve **bölü akor** olarak yazılır: C/E, "basta {{pc:E}} olan {{c:C}} akoru" demektir. Bu tür akorlar bas hattının adım adım, akıcı ilerlemesini sağlar.`,
      ),
      list([
        `C/E: {{c:C}} akoru, basta 3'lüsü ({{pc:E}}).`,
        `C/G: {{c:C}} akoru, basta 5'lisi ({{pc:G}}).`,
        `Örnek bas hattı: {{pc:C}} – {{pc:B}} – {{pc:A}} – {{pc:G}}. Akorlar: C – G/B – Am – G.`,
      ]),
      tip(
        'info',
        'Bas uyuyor ama akor uymuyorsa',
        `Akor bir çevrim olabilir. Bas notasının üzerine kurulu akor yerine, o notayı içeren diğer aile akorlarını dene. Örneğin basta {{pc:E}} varsa akor {{c:Em}} olabileceği gibi C/E de olabilir.`,
      ),
      {
        t: 'quiz',
        q: `Bu dizide bas notası nasıl hareket ediyor?`,
        play: { prog: 'C G Am F' },
        options: [
          'Hep aynı kalıyor',
          '{{pc:C}} – {{pc:G}} – {{pc:A}} – {{pc:F}} şeklinde değişiyor',
          'Her akorda yukarı çıkıyor',
        ],
        answer: 1,
        explain: `Akorlar {{c:C}} – {{c:G}} – {{c:Am}} – {{c:F}}; bas notaları da kökleri takip ediyor.`,
      },
      go('Kök Notayı Bul', 'akor.egz.kok.1'),
      go('Kök Notayı Bul: tüm akorlar', 'akor.egz.kok.2'),
    ],
  },
  {
    id: 'a6',
    title: 'En sık akor dizileri',
    minutes: 8,
    summary: `Şarkıların büyük çoğunluğunda dönüp dolaşan akor dizilerini dinle ve Romen rakamlarıyla tanı.`,
    blocks: [
      p(
        `Binlerce şarkı aynı birkaç akor dizisini kullanır. Bu dizileri tanıdığında yeni bir şarkı dinlerken "bu tanıdık" diyebilir ve akorları dakikalar içinde çıkarabilirsin. Dizileri Romen rakamlarıyla öğrenmek, onları her tonaliteye taşımanı sağlar.`,
      ),
      table(
        ['Dizi', 'Örnek', 'Dinle', 'Nerede?'],
        [
          [
            'I – V – vi – IV',
            '{{c:C}} {{c:G}} {{c:Am}} {{c:F}}',
            cell({ prog: 'C G Am F' }),
            'Pop müziğin en yaygın dizisi.',
          ],
          [
            'I – IV – V – I',
            '{{c:G}} {{c:C}} {{c:D}} {{c:G}}',
            cell({ prog: 'G C D G' }),
            'Halk müziği, rock ve country temeli.',
          ],
          [
            'vi – IV – I – V',
            '{{c:Em}} {{c:C}} {{c:G}} {{c:D}}',
            cell({ prog: 'Em C G D' }),
            'Duygusal pop ve rock baladları.',
          ],
          [
            'I – vi – IV – V',
            '{{c:C}} {{c:Am}} {{c:F}} {{c:G}}',
            cell({ prog: 'C Am F G' }),
            "1950'lerin klasik dizisi.",
          ],
          [
            'ii – V – I',
            '{{c:Dm7}} {{c:G7}} {{c:Cmaj7}}',
            cell({ prog: 'Dm7 G7 Cmaj7 Cmaj7' }),
            'Cazın temel kadansı.',
          ],
          [
            'i – iv – V',
            '{{c:Am}} {{c:Dm}} {{c:E}}',
            cell({ prog: 'Am Dm E Am' }),
            'Türk pop, rock ve arabeskte çok yaygın.',
          ],
          [
            'i – VII – VI – V',
            '{{c:Am}} {{c:G}} {{c:F}} {{c:E}}',
            cell({ prog: 'Am G F E' }),
            'Endülüs kadansı; flamenko ve rock.',
          ],
          [
            'i – VI – III – VII',
            '{{c:Am}} {{c:F}} {{c:C}} {{c:G}}',
            cell({ prog: 'Am F C G' }),
            'Destansı ve duygusal minör şarkılar.',
          ],
        ],
      ),
      tip(
        'info',
        'Aynı akorlar, farklı ev',
        `{{c:Am}} {{c:F}} {{c:C}} {{c:G}} dizisi {{pc:C}} majörde vi – IV – I – V, {{pc:A}} minörde i – VI – III – VII olarak okunur. Hangisi olduğunu, kulağının hangi akoru "ev" olarak duyduğu belirler.`,
      ),
      h(`12 ölçülük blues`),
      p(
        `Blues, rock'n'roll ve birçok rock şarkısı 12 ölçülük bir kalıbı tekrarlar. {{pc:A}} tonunda: dört ölçü {{c:A7}}, iki ölçü {{c:D7}}, iki ölçü {{c:A7}}, sonra {{c:E7}}, {{c:D7}}, {{c:A7}} ve dönüşte yine {{c:E7}}.`,
      ),
      table(
        ['Ölçüler', '1.', '2.', '3.', '4.'],
        [
          ['1–4', '{{c:A7}}', '{{c:A7}}', '{{c:A7}}', '{{c:A7}}'],
          ['5–8', '{{c:D7}}', '{{c:D7}}', '{{c:A7}}', '{{c:A7}}'],
          ['9–12', '{{c:E7}}', '{{c:D7}}', '{{c:A7}}', '{{c:E7}}'],
        ],
      ),
      play('12 ölçülük blues ({{pc:A}})', { prog: 'A7 A7 A7 A7 D7 D7 A7 A7 E7 D7 A7 E7', bpm: 120 }),
      tip(
        'success',
        'Nasıl çalışmalı?',
        `Her gün bir diziyi farklı bir tonalitede çal ve dinle. Bir şarkı dinlerken hangi diziye benzediğini tahmin etmeye çalış.`,
      ),
      go('Dizi Çıkar: I, IV, V', 'akor.egz.dizi.1'),
      go('Dizi Çıkar: vi eklendi', 'akor.egz.dizi.2'),
    ],
  },
  {
    id: 'a7',
    title: 'Akor ne zaman değişir?',
    minutes: 5,
    summary: `Akorları doğru bulmak kadar, değişimlerin hangi vuruşta olduğunu duymak da önemlidir.`,
    blocks: [
      p(
        `Akorların ne sıklıkla değiştiğine **armonik ritim** denir. Bir şarkıyı çıkarırken akorları doğru bulsan bile yanlış yerde değiştirirsen şarkı tanınmaz.`,
      ),
      h(`Vuruşları say`),
      p(
        `Çoğu pop ve rock şarkısı **4/4** ölçüdedir: her ölçüde dört vuruş vardır. Şarkıyı dinlerken ayağınla vuruşları takip et ve "1-2-3-4" diye say. Ölçünün ilk vuruşu genelde biraz daha vurgulu duyulur.`,
      ),
      play('Her ölçüde bir akor (4 vuruş)', { prog: 'C G Am F', bpm: 90 }),
      list([
        `Akorlar en çok ölçünün **1. vuruşunda** değişir.`,
        `Hızlı geçişlerde ölçü ikiye bölünür ve akor 1. ve 3. vuruşta değişir.`,
        `Armonik ritim bölümden bölüme değişebilir: kıtada akorlar iki ölçü sürerken nakaratta her ölçüde değişebilir.`,
      ]),
      h(`İpuçları`),
      list([
        `**Bas hareketi:** Bas notası değişiyorsa akor da büyük ihtimalle değişmiştir.`,
        `**Davul:** Ölçü başındaki vurgular, özellikle bölüm başlarındaki zil vuruşları, değişim anını işaret eder.`,
        `**Melodideki uzun notalar:** Melodide uzun tutulan notalar çoğu zaman o anki akorun notasıdır. Bu, akorun türünü bulmana da yardım eder.`,
      ]),
      tip(
        'info',
        'Metronomla çalış',
        `Akor geçişlerini metronomla ve yavaş bir tempoda (örneğin 60–70 BPM) çalışmak, değişimi tam ölçü başında yapmayı alışkanlık haline getirir.`,
      ),
      {
        t: 'quiz',
        q: `Bu dizide akorlar ne sıklıkla değişiyor?`,
        play: { prog: 'G D Em C' },
        options: ['Her vuruşta', 'Her ölçüde (4 vuruşta bir)', 'İki ölçüde bir'],
        answer: 1,
        explain: `Her akor bir ölçü, yani dört vuruş sürüyor.`,
      },
      go('Metronomu aç', 'araclar.metronom'),
      go('Şarkılarda dene', 'akor.sarkilar'),
    ],
  },
  {
    id: 'a8',
    title: 'Bir şarkıyı baştan sona çıkarmak',
    minutes: 7,
    summary: `Akorlarını bilmediğin bir şarkıyı baştan sona çıkarmak için adım adım bir yöntem.`,
    blocks: [
      p(
        `Bir şarkıyı çıkarmak büyük bir iş gibi görünür, ama küçük adımlara bölündüğünde düzenli bir sürece dönüşür. Aşağıdaki sırayı izle; her adım bir sonrakini kolaylaştırır.`,
      ),
      steps([
        [
          'Toniği bul',
          `Şarkının "ev" notasını bul. Nakaratın ya da şarkının son notasını mırıldan ve sapta ara. Bu nota genellikle toniktir.`,
        ],
        [
          'Majör mü minör mü?',
          `Toniğin üzerinde majör ve minör akoru ayrı ayrı çal. Şarkının genel havasına hangisi uyuyorsa tonalite odur.`,
        ],
        [
          'Akor ailesini yaz',
          `Tonalitenin yedi diyatonik akorunu bir kağıda yaz. Tahminlerin önce bu listeden gelsin.`,
        ],
        [
          'Bölümlere ayır',
          `Şarkıyı giriş, kıta, nakarat ve köprü gibi bölümlere ayır. Tekrar eden bir bölümü bir kez çıkarman yeterli.`,
        ],
        ['Bası takip et', `Her bölümde bas notalarını ve değişim anlarını bul. Bas notası çoğu zaman akorun köküdür.`],
        [
          'Adayları çalarak dene',
          `Her bas notası için aileden uygun akoru çal. Uymuyorsa önce türünü (majör/minör), sonra 7'li ya da sus gibi renkleri dene.`,
        ],
        [
          'Ödünç akorlara dikkat et',
          `Aileye uymayan bir akor duyarsan telaşlanma. Majörde ♭VII ve iv, minörde majör V çok sık kullanılan "misafir" akorlardır.`,
        ],
        [
          'Yaz ve baştan sona çal',
          `Bulduğun akorları ölçüleriyle birlikte yaz ve şarkıyla birlikte baştan sona çal. Takıldığın yerleri yeniden dinle.`,
        ],
      ]),
      tip(
        'info',
        'Bir örnek',
        `Şarkı {{pc:G}} notasında bitiyor ve majör duyuluyor. Aile: {{c:G}} {{c:Am}} {{c:Bm}} {{c:C}} {{c:D}} {{c:Em}}. Kıtada bas {{pc:G}} – {{pc:D}} – {{pc:E}} – {{pc:C}} şeklinde gidiyorsa akorlar büyük ihtimalle {{c:G}} – {{c:D}} – {{c:Em}} – {{c:C}} olur.`,
      ),
      play('Örnekteki kıtayı dinle', { prog: 'G D Em C', bpm: 92 }),
      tip(
        'success',
        'Kulağına güven, ama doğrula',
        `Bulduğun akoru şarkıyla birlikte çaldığında bir sürtünme ya da uyumsuzluk duyuyorsan bir şey eksik demektir. Akor tam oturduğunda şarkının içinde kaybolur gibi olur.`,
      ),
      tip(
        'info',
        'Uygulamadaki şarkılar',
        `Şarkılar bölümünde bu uygulama için bestelenmiş özgün şarkılar var. Her birini önce bölüm bölüm çıkar, sonra mikrofon açıkken baştan sona birlikte çal.`,
      ),
      go('Şarkılara git', 'akor.sarkilar'),
      go('Dizi Çıkar alıştırması', 'akor.egz.dizi.3'),
    ],
  },
  {
    id: 'a9',
    title: 'Kapo ve transpoze',
    minutes: 6,
    summary: `Kapoyla aynı akor şekillerini farklı tonalitelerde çalmayı ve şarkıları derecelerle düşünmeyi öğren.`,
    blocks: [
      p(
        `**Kapo**, sapı belli bir perdede bütün tellere birden basan bir aparattır. Kapoyu taktığın her perde, çaldığın her şeyi **yarım ses** yükseltir. Akor şekillerin aynı kalır, ama sesleri değişir.`,
      ),
      table(
        ['Çaldığın şekiller', 'Kapo', 'Duyulan tonalite'],
        [
          ['{{c:G}} şekilleri', '1. perde', '{{pc:Ab}} majör'],
          ['{{c:G}} şekilleri', '2. perde', '{{pc:A}} majör'],
          ['{{c:G}} şekilleri', '3. perde', '{{pc:Bb}} majör'],
          ['{{c:D}} şekilleri', '2. perde', '{{pc:E}} majör'],
          ['{{c:C}} şekilleri', '3. perde', '{{pc:Eb}} majör'],
          ['{{c:Am}} şekilleri', '2. perde', '{{pc:B}} minör'],
        ],
      ),
      p(
        `Örneğin şarkı {{pc:Bb}} majördeyse ve {{c:Bb}} barre akorunu çalmak istemiyorsan, kapoyu 3. perdeye tak ve {{c:G}} – {{c:C}} – {{c:D}} – {{c:Em}} şekillerini çal. Duyulan akorlar {{c:Bb}} – {{c:Eb}} – {{c:F}} – {{c:Gm}} olur.`,
      ),
      compare([
        ['G şekilleri, kapo yok', { prog: 'G C D G' }],
        ['Aynı şekiller, kapo 3', { prog: 'Bb Eb F Bb' }],
      ]),
      h(`Romen rakamlarıyla düşün`),
      p(
        `Akorları harflerle değil derecelerle ezberlersen transpoze etmek kolaylaşır. {{c:G}} – {{c:C}} – {{c:D}} dizisi aslında **I – IV – V** dizisidir. Aynı dizi {{pc:A}} majörde {{c:A}} – {{c:D}} – {{c:E}}, {{pc:E}} majörde {{c:E}} – {{c:A}} – {{c:B}} olur.`,
      ),
      steps([
        ['Dereceleri yaz', `Şarkının akorlarını tonalitedeki dereceleriyle yaz: örneğin I – vi – IV – V.`],
        [
          'Yeni tonaliteyi seç',
          `Şarkıcının sesine uyan tonaliteyi seç. Şarkıyı birkaç farklı tonda söyleyip en rahatını bulmak iyi bir yöntemdir.`,
        ],
        [
          'Aileden yerleştir',
          `Yeni tonalitenin akor ailesinden aynı dereceleri al: {{pc:D}} majörde I – vi – IV – V = {{c:D}} – {{c:Bm}} – {{c:G}} – {{c:A}}.`,
        ],
        [
          'Gerekirse kapo kullan',
          `Yeni tonalite zor akorlar gerektiriyorsa kolay şekillerin olduğu bir tonalite seç ve kapoyla yükselt.`,
        ],
      ]),
      tip(
        'info',
        'Neden önemli?',
        `Birlikte çaldığın bir şarkıcı "biraz daha pes olsun" dediğinde, dereceleri bilen bir gitarcı şarkıyı saniyeler içinde yeni tonaliteye taşıyabilir.`,
      ),
      {
        t: 'quiz',
        q: `Kapo 2. perdedeyken {{c:Em}} şeklini çalıyorsun. Duyulan akor hangisi?`,
        options: ['{{c:F#m}}', '{{c:Fm}}', '{{c:Gm}}'],
        answer: 0,
        explain: `Kapo 2 her şeyi iki yarım ses yükseltir: {{pc:E}} → {{pc:F}} → {{pc:F#}}. Duyulan akor {{c:F#m}}.`,
      },
      go('Akor kütüphanesi', 'araclar.kutuphane'),
    ],
  },
  {
    id: 'a10',
    title: 'Renkli akorlar',
    minutes: 8,
    summary: `Üçlü akorların ötesine geç: 7'li akorları, sus akorlarını ve tonalite dışından gelen ödünç akorları kulakla tanı.`,
    blocks: [
      p(
        `Temel üçlü akorlara eklenen notalar akorlara renk katar. Akor çıkarırken bası ve türü doğru bulduğun halde "bir şey eksik" diyorsan, aradığın büyük ihtimalle bu renklerden biridir.`,
      ),
      h(`7'li akorlar`),
      table(
        ['Akor', 'Notalar', 'Karakter', 'Dinle'],
        [
          [
            "{{c:G7}} (dominant 7'li)",
            '{{pc:G}} {{pc:B}} {{pc:D}} {{pc:F}}',
            "Gergin, çözülmek ister; blues ve rock'ta çok yaygın.",
            cell({ chord: 'G7' }),
          ],
          [
            "{{c:Cmaj7}} (majör 7'li)",
            '{{pc:C}} {{pc:E}} {{pc:G}} {{pc:B}}',
            'Yumuşak ve hayalperest; caz ve bossa nova havası.',
            cell({ chord: 'Cmaj7' }),
          ],
          [
            "{{c:Am7}} (minör 7'li)",
            '{{pc:A}} {{pc:C}} {{pc:E}} {{pc:G}}',
            'Minörün daha yumuşak, daha az hüzünlü hali.',
            cell({ chord: 'Am7' }),
          ],
        ],
      ),
      compare([
        ['C', { chord: 'C' }],
        ['Cmaj7', { chord: 'Cmaj7' }],
        ['C7', { chord: 'C7' }],
      ]),
      h(`Sus akorları: askıda kalan 3'lü`),
      p(
        `Sus akorlarında 3'lü yoktur; yerine 2'li (sus2) ya da 4'lü (sus4) gelir. Bu yüzden ne majör ne minör duyulurlar, askıda kalırlar. Sus4 akoru çoğu zaman 3'lüye çözülür: {{c:Dsus4}} → {{c:D}}.`,
      ),
      compare([
        ['Dsus4 → D', { prog: 'Dsus4 D' }],
        ['Asus4 → A', { prog: 'Asus4 A' }],
        ['Dsus2', { chord: 'Dsus2' }],
      ]),
      h(`Ödünç akorlar`),
      p(
        `Bazı akorlar tonalitenin ailesinden değildir; paralel tonaliteden "ödünç" alınır. Majör bir şarkıda en sık duyacağın iki misafir **iv** (minör dördüncü) ve **♭VII** akorlarıdır.`,
      ),
      compare([
        ['C – F – Fm – C (iv)', { prog: 'C F Fm C' }],
        ['C – B♭ – F – C (♭VII)', { prog: 'C Bb F C' }],
      ]),
      h(`İkincil dominant`),
      p(
        `Bir akordan önce, o akorun "kendi V akoru" çalınabilir. {{pc:C}} majörde {{c:Am}} akorundan önce gelen {{c:E7}} buna örnektir: {{c:E7}}, {{c:Am}} akorunun dominantıdır ve ona güçlü bir çekim yaratır. Ailede olmayan {{pc:G#}} notası bu yüzden kulağa çarpar.`,
      ),
      play('C – E7 – Am – F', { prog: 'C E7 Am F', bpm: 90 }),
      tip(
        'info',
        'Kulakla nasıl yakalarsın?',
        `Bildiğin aileden hiçbir akor uymuyorsa bası bul ve o bas üzerinde önce majör, sonra minör, sonra 7'li akoru dene. Ödünç akorlar çoğu zaman beklenmedik bir renk değişimi olarak duyulur: ani bir hüzün (iv) ya da ani bir genişlik (♭VII).`,
      ),
      go("Majör mü Minör mü? 7'liler", 'akor.egz.kalite.3'),
      go('Derece Tanıma: karışık', 'akor.egz.derece.5'),
    ],
  },
];
